import {
  createPayment,
  deleteUser,
  fetchPayments,
  fetchStats,
  getUser,
  fetchFriendsList,
  fetchFriendRequests,
  postFriendRequest,
  postFriendRequestResponse,
  updatePayment,
  updateStats,
  userLogin,
  updatePassword,
  userRegistration,
} from "../../../services/Api/index.ts";

// register and login
export const registerUser = async (
  email,
  password,
  username,
  setToken,
  setUserData
) => {
  try {
    const response = await userRegistration({
      email,
      password,
      username,
      setToken,
      setUserData,
    });
    if (response.ok) {
      const data = await response.json();
      const token = data.accessToken;
      localStorage.setItem("accessToken", token);
      loginUser(email, password, setToken, setUserData);

      return null; // No error
    } else {
      const errorResponse = await response.json();
      return errorResponse.message; // Return the error message
    }
  } catch (error) {
    console.error("Registration error:", error);
    return "The DRAGON must have burnt down the servers... try again";
  }
};

export const loginUser = async (email, password, setToken, setUserData) => {
  try {
    const response = await userLogin({ email, password });
    if (response.ok) {
      const data = await response.json();
      const token = data.accessToken;
      localStorage.setItem("accessToken", token);
      setToken(token);
      await getUserInfo(token, setUserData);
      return null; // No error
    } else {
      const errorResponse = await response.json();
      return errorResponse.message; // Return the error message
    }
  } catch (error) {
    return "The DRAGON must have burnt down the servers... try again";
  }
};

// user info
export const getUserInfo = async (token, setUserData) => {
  try {
    const response = await getUser(token); // Pass token directly to getUser function
    if (response.ok) {
      const data = await response.json();
      localStorage.setItem("userData", JSON.stringify(data));
      setUserData(data);
      return null; // No error
    } else {
      const errorResponse = await response.json();
      return errorResponse.message; // Return the error message
    }
  } catch (error) {
    return "The DRAGON must have burnt down the servers... make a payment and try again when you are finished";
  }
};

// delete user account
export const deleteAccount = async (token, userId, handleSignOut) => {
  try {
    const response = await deleteUser(token, userId);
    if (response.ok) {
      handleSignOut();
    } else {
      const errorText = await response.text();
      console.error("Error deleting user:", errorText);
    }
  } catch {}
};

//update user info
export const sendUpdatedPassword = async (
  accessToken,
  currentPassword,
  newPassword,
  setDisplayError,
  setSnackBarDisplayMessage,
  setSnackBarDisplaySeverity
) => {
  try {
    const response = await updatePassword({
      token: accessToken,
      body: { currentPassword: currentPassword, newPassword: newPassword },
    });
    if (response.ok) {
      return true;
      // setSnackBarDisplaySeverity("success");
      // setSnackBarDisplayMessage("Friend request sent!");
    } else {
      const errorResponse = await response.json(); // Always parse JSON here
      console.error("updatePassword error", errorResponse);
      const errorMessage =
        errorResponse.error ||
        errorResponse.message ||
        "The DRAGON must be destroying the server room... try again";
      setDisplayError(errorMessage);
    }
  } catch (error) {
    console.error("updatePassword error", error);
    setDisplayError("The DRAGON must be destroying the servers... try again");
    // setSnackBarDisplaySeverity("error");
    // setSnackBarDisplayMessage("server error: friend request was not sent"); // Catch-all for other errors
  }
};

//stats
export const getStats = async (
  token,
  userId,
  setUserStats,
  setDisplayError
) => {
  try {
    const response = await fetchStats({
      token: token,
      userId: userId,
    });
    const statData = await response.json();
    //new user has no stats
    if (!response.ok) {
      const errorMessage = await response.text(); // Get the error message from the response body
      setDisplayError(errorMessage); // Assuming setDisplayError handles displaying the error message
      return; // Exit the function or handle further as needed
    }
    localStorage.setItem("userStats", JSON.stringify(statData));
    setUserStats(statData);
  } catch (error) {
    console.error("Error fetching payments:", error);
    setDisplayError(
      "The DRAGON must have burnt down the servers... make a payment irl and try again when you are finished"
    );
    return;
  }
};

export const updateStatBoolean = async (
  accessToken,
  userId,
  allDebtsPaid,
  setDisplayError,
  setUserStats
) => {
  const userStatsString = localStorage.getItem("userStats");
  const userStats = userStatsString ? JSON.parse(userStatsString) : null;

  if (!userStats || userStats.allDailyComplete) {
    return;
  }
  try {
    const formattedBoolean = {
      allDailyComplete: allDebtsPaid,
    };
    const response = await updateStats({
      token: accessToken,
      userId: userId,
      body: formattedBoolean,
    });
    if (response.ok) {
      localStorage.setItem("putDateOfStatBoolean", new Date().toISOString());
      getStats(accessToken, userId, setUserStats, setDisplayError);
    } else {
      const errorResponse = await response.json();
      console.error("updateStatBoolean error", errorResponse);
      setDisplayError(
        "Server error: the completion status was not updated on the server."
      );
    }
  } catch (error) {
    console.error("updateStatBoolean error", error);
    setDisplayError(
      "Server error: the completion status was not updated on the server."
    );
  }
};

//payments
export const getPayments = async (
  token,
  userId,
  setStarted,
  setSavedPaymentList,
  setUserStats,
  setDisplayError
) => {
  try {
    const response = await fetchPayments({
      token: token,
      userId: userId,
    });
    const paymentData = await response.json();
    if (paymentData.length >= 1) {
      localStorage.setItem("userPayments", JSON.stringify(paymentData));
      setSavedPaymentList(paymentData);
      localStorage.setItem("fetchDateOfUserPayment", new Date().toISOString());
    }
    //new user has no payments
    if (response.status === 404) {
      localStorage.removeItem("userPayments");
      setStarted(true);
      getStats(token, userId, setStarted, setUserStats, setDisplayError);
      return;
    }
    setStarted(true);
    getStats(token, userId, setStarted, setUserStats, setDisplayError);
  } catch (error) {
    console.error("Error fetching payments:", error);
    setDisplayError(
      "The DRAGON must have burnt down the servers... make a payment irl and try again when you are finished"
    );
    return;
  }
};

export const sendPayments = async (
  tempPaymentList,
  accessToken,
  userId,
  setStarted,
  setSavedPaymentList,
  setDisplayError
) => {
  try {
    const formattedPayments = {
      payments: tempPaymentList.map((payment) => ({
        paymentName: payment.paymentName,
        complete: Boolean(payment.complete),
      })),
    };
    const response = await createPayment({
      token: accessToken,
      userId: userId,
      body: formattedPayments,
    });
    if (response.ok) {
      getPayments(accessToken, userId, setStarted, setSavedPaymentList);
    } else if (response.status === 400) {
      // Handle specific error for status code 400
      const errorResponse = await response.json();
      console.error("sendPayments error (400)", errorResponse);
      const errorMessage =
        errorResponse.error ||
        "The DRAGON must be destroying the server room... try again";
      setDisplayError(errorMessage);
    } else {
      const errorResponse = await response.json();
      console.error("sendPayments error", errorResponse);
      setDisplayError(
        "The DRAGON must be destroying the server room... try again"
      );
    }
  } catch (error) {
    console.error("sendPayments error", error);
    setDisplayError("The DRAGON must be destroying the servers... try again");
  }
};

export const updatePayments = async (
  tempSavedPaymentList,
  accessToken,
  userId,
  setDisplayError
) => {
  try {
    const formattedPayments = {
      payments: tempSavedPaymentList.map((payment) => ({
        paymentName: payment.paymentName,
        complete: Boolean(payment.complete),
      })),
    };
    const response = await updatePayment({
      token: accessToken,
      userId: userId,
      body: formattedPayments,
    });
    if (response.ok) {
      console.log("response", response);
    } else {
      const errorResponse = await response.json();
      console.error("sendPayments error", errorResponse);
      setDisplayError(
        "Server error: the completion status was not updated on the server."
      );
    }
  } catch (error) {
    console.error("sendPayments error", error);
    setDisplayError(
      "Server error: the completion status was not updated on the server."
    );
  }
};

//friends
export const sendFriendRequest = async (
  accessToken,
  setDisplayError,
  requestedUsername,
  setSnackBarDisplayMessage,
  setSnackBarDisplaySeverity,
  setRequestedUsername
) => {
  try {
    const response = await postFriendRequest({
      token: accessToken,
      body: { receiverUsername: requestedUsername },
    });
    if (response.ok) {
      setSnackBarDisplaySeverity("success");
      setSnackBarDisplayMessage("Friend request sent!");
      setRequestedUsername("");
    } else {
      const errorResponse = await response.json(); // Always parse JSON here
      console.error("postFriendRequest error", errorResponse);

      // Correctly check status codes
      if (response.status === 400) {
        console.error("postFriendRequest error", errorResponse);
        setSnackBarDisplaySeverity("error");
        setSnackBarDisplayMessage("request error, please try again");
      } else if (response.status === 409) {
        console.error(
          "postFriendRequest error - duplicate request",
          errorResponse
        );
        setSnackBarDisplaySeverity("info");
        setSnackBarDisplayMessage("request already sent to user");
      } else if (response.status === 422) {
        console.error(
          "postFriendRequest error - username not found",
          errorResponse
        );
        setSnackBarDisplaySeverity("warning");
        setSnackBarDisplayMessage("username not found");
      } else {
        console.error("postFriendRequest error", errorResponse);
        setSnackBarDisplaySeverity("error");
        setSnackBarDisplayMessage("server error: friend request was not sent"); // Catch-all for other errors
      }
    }
  } catch (error) {
    console.error("postFriendRequest error", error);
    setDisplayError("The DRAGON must be destroying the servers... try again");
    setSnackBarDisplaySeverity("error");
    setSnackBarDisplayMessage("server error: friend request was not sent"); // Catch-all for other errors
  }
};

export const getFriendRequests = async (token) => {
  try {
    const response = await fetchFriendRequests(token);
    if (response.ok) {
      const data = await response.json();
      localStorage.setItem("pendingFriendRequests", JSON.stringify(data));
      return null; // No error
    } else {
      const errorResponse = await response.json();
      return errorResponse.message; // Return the error message
    }
  } catch (error) {
    return "The DRAGON must have burnt down the servers... make a payment and try again when you are finished";
  }
};

export const getFriendsList = async (token) => {
  try {
    const response = await fetchFriendsList(token);
    if (response.ok) {
      const data = await response.json();
      localStorage.setItem("friendsList", JSON.stringify(data));
      localStorage.setItem("fetchDateOfFriendsList", new Date().toISOString());
      return null; // No error
    } else {
      const errorResponse = await response.json();
      return errorResponse.message; // Return the error message
    }
  } catch (error) {
    return "The DRAGON must have burnt down the servers... make a payment and try again when you are finished";
  }
};

export const sendFriendRequestResponse = async (
  accessToken,
  responseAction,
  requestId,
  setDisplayError
  // setSnackBarDisplayMessage,
  // setSnackBarDisplaySeverity
) => {
  try {
    const response = await postFriendRequestResponse({
      token: accessToken,
      requestId: requestId,
      body: { action: responseAction },
    });
    if (response.ok) {
      if (responseAction === "accept") {
        getFriendsList(accessToken);
      }
      // setSnackBarDisplaySeverity("success");

      // setSnackBarDisplayMessage("Friend request sent!");
    } else {
      const errorResponse = await response.json(); // Always parse JSON here
      console.error("postFriendRequest error", errorResponse);

      // Correctly check status codes
      if (response.status === 400) {
        console.error("postFriendRequest error", errorResponse);
        // setSnackBarDisplaySeverity("error");
        // setSnackBarDisplayMessage("request error, please try again");
      } else {
        console.error("postFriendRequest error", errorResponse);
        // setSnackBarDisplaySeverity("error");
        // setSnackBarDisplayMessage("server error: try again"); // Catch-all for other errors
      }
    }
  } catch (error) {
    console.error("postFriendRequest error", error);
    setDisplayError("The DRAGON must be destroying the servers... try again");
    // setSnackBarDisplaySeverity("error");
    // setSnackBarDisplayMessage("server error: friend request was not sent"); // Catch-all for other errors
  }
};
